/**
 * Client helpers for profile / listing photo and text moderation.
 */

export const PROFILE_IMAGE_BLOCKED = 'profile_image_blocked';
export const PROFILE_IMAGE_UNAVAILABLE = 'profile_image_unavailable';
export const PROFILE_TEXT_BLOCKED = 'profile_text_blocked';

export const PHOTO_BLOCKED_MESSAGE = "This photo can't be used. Please choose a different one.";
export const PHOTO_UNAVAILABLE_MESSAGE = "Couldn't check this photo, try again.";
export const TEXT_BLOCKED_MESSAGE = "Please reword this. It doesn't meet our Community Guidelines.";

export const profileModerationCode = error =>
  error?.code || error?.data?.code || error?.apiErrors?.[0]?.code || null;

export const isProfileImageBlockedError = error =>
  profileModerationCode(error) === PROFILE_IMAGE_BLOCKED;

export const isProfileImageUnavailableError = error =>
  profileModerationCode(error) === PROFILE_IMAGE_UNAVAILABLE;

export const isProfileTextBlockedError = error =>
  profileModerationCode(error) === PROFILE_TEXT_BLOCKED;

export const profileImageErrorMessage = error => {
  if (isProfileImageBlockedError(error)) {
    return PHOTO_BLOCKED_MESSAGE;
  }
  if (isProfileImageUnavailableError(error)) {
    return PHOTO_UNAVAILABLE_MESSAGE;
  }
  return null;
};

const BLOCK_MESSAGE_RE = /community guidelines|reword|please remove|adults 18 and over/i;

/**
 * Field + plain-language reason for a blocked profile text, or null.
 *
 * @param {Object} error
 * @returns {{ field: string|null, reason: string|null, message: string }|null}
 */
export const profileTextBlockInfo = error => {
  const coded = isProfileTextBlockedError(error);
  const msg = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!coded && !(msg && /community guidelines|reword this/i.test(msg))) {
    return null;
  }
  return {
    field: error?.field || error?.data?.field || null,
    reason: error?.reason || error?.data?.reason || null,
    message: msg && BLOCK_MESSAGE_RE.test(msg) ? msg : TEXT_BLOCKED_MESSAGE,
    blockedValue: typeof error?.blockedValue === 'string' ? error.blockedValue : null,
  };
};

export const profileTextErrorMessage = error => profileTextBlockInfo(error)?.message || null;

/**
 * Read a File as a data URL for the upload proxy.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
export const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('A photo file is required.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read this photo.'));
    reader.readAsDataURL(file);
  });
